"use client";

import { Box, Link as MuiLink, Typography, Container } from "@mui/material";
import React from "react";
import ArrowRightAlt from "@mui/icons-material/ArrowRightAlt";
import { usePathname } from "next/navigation";
import { Link } from "@/i18n/routing";
import { InterlinkSection } from "@/types/dto/common/common-interlinking";
import CommonInterlinkingSkeleton from "./common-interlinking-skeleton";

interface InterlinkingMenuItem {
  label: string;
  href: string;
}

interface CommonInterlinkingProps {
  dataModuleRanking?: number;
  isLoading?: boolean;
  interlinkData?: InterlinkSection[];
  headingText?: string;
  ariaLabel?: string;
  parentContainerType?: string;
  moduleName?: string;
  testIdPrefix?: string;
}

const CommonInterLinking = ({
  dataModuleRanking,
  isLoading = false,
  interlinkData,
  headingText = "Why Collette",
  ariaLabel = "Why Collette navigation",
  parentContainerType = "why-collette-interlinking",
  moduleName = "Interlinking Menu",
  testIdPrefix = "why-collette"
}: CommonInterlinkingProps) => {
  const dataParentContainerType = parentContainerType;
  const dataModuleName = moduleName;
  const pathname = usePathname();

  const moduleRanking = dataModuleRanking ?? -1;

  // Return skeleton while loading
  if (isLoading) {
    return <CommonInterlinkingSkeleton />;
  }

  // Convert API data to menu items format
  const menuItems: InterlinkingMenuItem[] = (interlinkData || []).map((item) => ({
    label: item.headerText,
    href: item.callToActionUrl || ""
  }));

  // Check if the current pathname matches the menu item href or if href is empty
  const isCurrentPage = (href: string): boolean =>
    !!href &&
    pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?/, "").replace(/\/+$/, "") ===
      href.replace(/\/+$/, "");

  // Check if the href is valid (not empty)
  const hasValidUrl = (href: string): boolean => !!href;

  return (
    <Box
      component="section"
      aria-labelledby="interlinking-heading"
      className="destination-page-container"
      data-testid={`${testIdPrefix}-interlinking-menu`}
      data-track="visibility"
      data-parent-container-type={dataParentContainerType}
      data-module-name={dataModuleName}
      data-module-ranking-position={moduleRanking}
      data-ranking-algorithm="manual"
      sx={{ p: { xs: "4rem 2.4rem", sm: "4rem 3.2rem", lg: "4rem 10rem 6rem" } }}
    >
      <Typography
        id="interlinking-heading"
        data-testid="interlinking-header"
        variant="h2"
        sx={{
          mb:"4rem",
          fontSize: { xs: "2.6rem", sm: "2.8rem", lg: "3.2rem" },
          lineHeight: 1.3,
          textAlign: "center"
        }}
      >
        {headingText}
      </Typography>

      <Container maxWidth={false} disableGutters>
        <Box component="nav" aria-label={ariaLabel} data-testid={`${testIdPrefix}-nav`}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "repeat(5, 1fr)"
              },
              gap: "2rem"
            }}
          >
            {menuItems.map((item) => {
              const valid = hasValidUrl(item.href);
              const currentPageFlag = valid && isCurrentPage(item.href);
              const href = valid && !currentPageFlag ? item.href : undefined;

              return (
                <MuiLink
                  key={item.label}
                  component={currentPageFlag ? Box : Link}
                  {...(!currentPageFlag && href ? { href } : {})}
                  underline="none"
                  data-testid={`${testIdPrefix}-menu-item-${item.label.toLowerCase().replace(/&/g, "and").replace(/\s+/g, "-")}`}
                  {...(currentPageFlag && { "aria-current": "page" })}
                  onClick={
                    !valid ? (e: React.MouseEvent<HTMLDivElement>) => e.preventDefault() : undefined
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "1.6rem 2rem",
                    borderRadius: "2rem",
                    border: currentPageFlag ? "2px solid" : "none",
                    borderColor: currentPageFlag ? "primary.main" : undefined,
                    backgroundColor: currentPageFlag ? "background.cool_strong" : "background.warm",
                    color: "primary.main",
                    gap: "2rem",
                    cursor: currentPageFlag || !valid ? "default" : "pointer",
                    transition: "all 0.3s ease",
                    height: "100%",
                    textDecoration: "none",
                    boxSizing: "border-box",
                    opacity: valid ? 1 : 0.6,
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: "2px"
                    },
                    "&:hover": {
                      backgroundColor: "background.cool_strong"
                    }
                  }}
                >
                  <Typography variant="h5" sx={{ lineHeight: "normal" }}>
                    {item.label}
                  </Typography>

                  {!currentPageFlag ? (
                    <ArrowRightAlt
                      sx={{
                        width: "4rem",
                        height: "4rem"
                      }}
                    />
                  ) : (
                    // below is to account for missing height when arrow is gone to keep all items the same height
                    <Box height="4rem" width={0} />
                  )}
                </MuiLink>
              );
            })}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CommonInterLinking;
